import { emptyValueForSchema, getSchemaAt, CASE_TYPE_SCHEMA } from "../../../../case-spec";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ArrayShell,
  BoolField,
  EnumSelect,
  MediaUrlField,
  RowCard,
  StringListField,
  TextField,
  useOpenIndex,
} from "../shared-ui";
import {
  asArray,
  bool,
  enumOptionsAt,
  isRecord,
  str,
  type PanelProps,
} from "../shared";
import {
  appendAtPath,
  childPath,
  deleteAtPath,
  getAtPath,
  setAtPath,
} from "../path-utils";

/** Shared EXIF defaults; override per shot only when needed. */
const DEFAULT_EXIF_CAMERA = "iPhone";
const DEFAULT_EXIF_LENS = "wide";

const TABS = [
  { key: "photos", label: "照片", path: "device.apps.photos.photos" },
  {
    key: "screenshots",
    label: "截图",
    path: "device.apps.photos.screenshots",
  },
  { key: "videos", label: "视频", path: "device.apps.photos.videos" },
  { key: "albums", label: "相册", path: "device.apps.photos.albums" },
  { key: "hidden", label: "隐藏照片", path: "device.apps.photos.hidden" },
  {
    key: "recently_deleted",
    label: "最近删除",
    path: "device.apps.photos.recently_deleted",
  },
] as const;

export function PhotosPanel({ root, disabled, onChangeRoot }: PanelProps) {
  const [tab, setTab] = useState<(typeof TABS)[number]["key"]>("photos");
  const active = TABS.find((t) => t.key === tab) ?? TABS[0];
  const items = asArray(getAtPath(root, active.path));
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(items.length);

  return (
    <div className="flex flex-col gap-3">
      <TextField
        label="照片密码"
        value={str(getAtPath(root, "device.apps.photos.passcode"))}
        placeholder="6 位数字"
        disabled={disabled}
        onChange={(value) =>
          onChangeRoot(setAtPath(root, "device.apps.photos.passcode", value))
        }
      />
      <div className="flex flex-wrap gap-1">
        {TABS.map((t) => {
          const n = asArray(getAtPath(root, t.path)).length;
          return (
            <Button
              key={t.key}
              type="button"
              size="sm"
              variant={tab === t.key ? "secondary" : "ghost"}
              onClick={() => {
                setTab(t.key);
                setOpenIndex(null);
              }}
            >
              {t.label}
              <span className="ml-1 text-muted-foreground">{n}</span>
            </Button>
          );
        })}
      </div>

      <ArrayShell
        title={active.label}
        count={items.length}
        disabled={disabled}
        onAdd={() => {
          const schema = getSchemaAt(CASE_TYPE_SCHEMA, `${active.path}[0]`);
          const row = withPhotoDefaults(
            active.key,
            schema ? emptyValueForSchema(schema) : defaultRow(active.key),
          );
          onChangeRoot(appendAtPath(root, active.path, row));
          setOpenIndex(items.length);
        }}
      >
        <ul className="flex flex-col gap-2">
          {items.map((item, index) => {
            const rec = isRecord(item) ? item : {};
            const itemPath = childPath(active.path, index);
            const title = rowTitle(active.key, rec, index);
            return (
              <RowCard
                key={itemPath}
                title={title}
                open={openIndex === index}
                onToggle={() => toggle(index)}
                disabled={disabled}
                index={index}
                total={items.length}
                arrayPath={active.path}
                root={root}
                onChangeRoot={onChangeRoot}
              >
                {active.key === "photos" ||
                active.key === "hidden" ||
                active.key === "recently_deleted" ? (
                  <PhotoFields
                    root={root}
                    itemPath={itemPath}
                    rec={rec}
                    disabled={disabled}
                    onChangeRoot={onChangeRoot}
                  />
                ) : null}
                {active.key === "screenshots" ? (
                  <ScreenshotFields
                    root={root}
                    itemPath={itemPath}
                    rec={rec}
                    disabled={disabled}
                    onChangeRoot={onChangeRoot}
                  />
                ) : null}
                {active.key === "videos" ? (
                  <VideoFields
                    root={root}
                    itemPath={itemPath}
                    rec={rec}
                    disabled={disabled}
                    onChangeRoot={onChangeRoot}
                  />
                ) : null}
                {active.key === "albums" ? (
                  <>
                    <TextField
                      label="名称"
                      value={str(rec.name)}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "name"), v),
                        )
                      }
                    />
                    <EnumSelect
                      label="类型"
                      fieldKey="type"
                      value={str(rec.type)}
                      options={enumOptionsAt(
                        "device.apps.photos.albums[0].type",
                      )}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "type"), v),
                        )
                      }
                    />
                  </>
                ) : null}
              </RowCard>
            );
          })}
        </ul>
      </ArrayShell>
    </div>
  );
}

function defaultRow(key: string): Record<string, unknown> {
  if (key === "albums") return { name: "", type: "custom" };
  if (key === "videos") {
    return {
      taken_at: "",
      type: "video",
    };
  }
  if (key === "screenshots") {
    return {
      taken_at: "",
      source: "other",
    };
  }
  return {
    taken_at: "",
    type: "camera",
    album: "",
    favorite: false,
    exif: { camera: DEFAULT_EXIF_CAMERA, lens: DEFAULT_EXIF_LENS },
  };
}

function withPhotoDefaults(
  key: string,
  row: unknown,
): Record<string, unknown> {
  const rec = isRecord(row) ? { ...row } : {};
  if (key === "photos" || key === "hidden" || key === "recently_deleted") {
    const exif = isRecord(rec.exif) ? { ...rec.exif } : {};
    if (!str(exif.camera)) exif.camera = DEFAULT_EXIF_CAMERA;
    if (!str(exif.lens)) exif.lens = DEFAULT_EXIF_LENS;
    rec.exif = exif;
  }
  return rec;
}

function setOptionalString(
  root: unknown,
  path: string,
  value: string,
): unknown {
  return value.trim()
    ? setAtPath(root, path, value)
    : deleteAtPath(root, path);
}

function rowTitle(
  key: string,
  rec: Record<string, unknown>,
  index: number,
): string {
  if (key === "albums") return str(rec.name) || `相册 ${index + 1}`;
  const when = str(rec.taken_at);
  const loc = str(rec.location);
  const desc = str(rec.content_description);
  if (desc) return desc.slice(0, 48) + (desc.length > 48 ? "…" : "");
  if (when && loc) return `${when} · ${loc}`;
  if (when) return when;
  return `${key} ${index + 1}`;
}

function PhotoFields({
  root,
  itemPath,
  rec,
  disabled,
  onChangeRoot,
}: {
  root: unknown;
  itemPath: string;
  rec: Record<string, unknown>;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
}) {
  const patch = (key: string, value: unknown) =>
    onChangeRoot(setAtPath(root, childPath(itemPath, key), value));
  const people = asArray(rec.people).map((p) => str(p));
  return (
    <>
      <TextField
        label="拍摄时间"
        fieldKey="taken_at"
        value={str(rec.taken_at)}
        disabled={disabled}
        placeholder="YYYY-MM-DDTHH:mm:ss"
        onChange={(v) => patch("taken_at", v)}
      />
      <EnumSelect
        label="类型"
        fieldKey="type"
        value={str(rec.type)}
        options={enumOptionsAt("device.apps.photos.photos[0].type")}
        disabled={disabled}
        onChange={(v) => patch("type", v)}
      />
      <TextField
        label="地点"
        value={str(rec.location)}
        disabled={disabled}
        onChange={(v) => patch("location", v)}
      />
      <TextField
        label="相册"
        value={str(rec.album)}
        disabled={disabled}
        onChange={(v) => patch("album", v)}
      />
      <StringListField
        label="人物"
        value={people}
        disabled={disabled}
        onChange={(v) => patch("people", v)}
      />
      <BoolField
        label="收藏"
        value={bool(rec.favorite)}
        disabled={disabled}
        onChange={(v) => patch("favorite", v)}
      />
      <MediaUrlField
        label="图片 URL"
        value={str(rec.image_url)}
        disabled={disabled}
        fieldKey="image_url"
        onChange={(v) => patch("image_url", v)}
      />
      <TextField
        label="EXIF 场景（选填）"
        value={str(getAtPath(root, childPath(itemPath, "exif.scene")))}
        disabled={disabled}
        onChange={(v) =>
          onChangeRoot(
            setOptionalString(root, childPath(itemPath, "exif.scene"), v),
          )
        }
      />
      <TextField
        label="EXIF 相机"
        value={str(getAtPath(root, childPath(itemPath, "exif.camera")))}
        disabled={disabled}
        placeholder={DEFAULT_EXIF_CAMERA}
        onChange={(v) =>
          onChangeRoot(setAtPath(root, childPath(itemPath, "exif.camera"), v))
        }
      />
      <TextField
        label="EXIF 镜头"
        value={str(getAtPath(root, childPath(itemPath, "exif.lens")))}
        disabled={disabled}
        placeholder={DEFAULT_EXIF_LENS}
        onChange={(v) =>
          onChangeRoot(setAtPath(root, childPath(itemPath, "exif.lens"), v))
        }
      />
    </>
  );
}

function ScreenshotFields({
  root,
  itemPath,
  rec,
  disabled,
  onChangeRoot,
}: {
  root: unknown;
  itemPath: string;
  rec: Record<string, unknown>;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
}) {
  const patch = (key: string, value: unknown) =>
    onChangeRoot(setAtPath(root, childPath(itemPath, key), value));
  return (
    <>
      <TextField
        label="截图时间"
        fieldKey="taken_at"
        value={str(rec.taken_at)}
        disabled={disabled}
        placeholder="YYYY-MM-DDTHH:mm:ss"
        onChange={(v) => patch("taken_at", v)}
      />
      <EnumSelect
        label="来源"
        fieldKey="source"
        value={str(rec.source)}
        options={enumOptionsAt("device.apps.photos.screenshots[0].source")}
        disabled={disabled}
        onChange={(v) => patch("source", v)}
      />
      <TextField
        label="内容说明（选填）"
        value={str(rec.content_description)}
        disabled={disabled}
        multiline
        rows={4}
        onChange={(v) =>
          onChangeRoot(
            setOptionalString(root, childPath(itemPath, "content_description"), v),
          )
        }
      />
      <MediaUrlField
        label="图片 URL"
        value={str(rec.image_url)}
        disabled={disabled}
        fieldKey="image_url"
        onChange={(v) => patch("image_url", v)}
      />
    </>
  );
}

function VideoFields({
  root,
  itemPath,
  rec,
  disabled,
  onChangeRoot,
}: {
  root: unknown;
  itemPath: string;
  rec: Record<string, unknown>;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
}) {
  const patch = (key: string, value: unknown) =>
    onChangeRoot(setAtPath(root, childPath(itemPath, key), value));
  return (
    <>
      <TextField
        label="拍摄时间"
        fieldKey="taken_at"
        value={str(rec.taken_at)}
        disabled={disabled}
        placeholder="YYYY-MM-DDTHH:mm:ss"
        onChange={(v) => patch("taken_at", v)}
      />
      <EnumSelect
        label="类型"
        fieldKey="type"
        value={str(rec.type)}
        options={enumOptionsAt("device.apps.photos.videos[0].type")}
        disabled={disabled}
        onChange={(v) => patch("type", v)}
      />
      <TextField
        label="地点"
        value={str(rec.location)}
        disabled={disabled}
        onChange={(v) => patch("location", v)}
      />
      <MediaUrlField
        label="视频 URL"
        value={str(rec.video_url)}
        disabled={disabled}
        mediaKind="video"
        fieldKey="video_url"
        onChange={(v) => patch("video_url", v)}
      />
    </>
  );
}
